import { useState, useMemo } from 'react';
import Modal from '../common/Modal';
import { orderApi } from '../../api/services';
import toast from 'react-hot-toast';
import { Plus, Trash2, Loader2 } from 'lucide-react';

/**
 * Split a single pending shipment into multiple shipments (sibling part-orders),
 * each with its own scheduled date.
 *
 * Single-item parcel: shows the legacy per-part qty entry. Per-item allocation
 *   is implicit (the one item is what gets split).
 *
 * Multi-item parcel: shows a per-item × per-part grid. User enters how much
 *   of each item goes in each part. Each item's row must sum to the original
 *   parcel qty for that item. Sends `items: [{orderItemId, quantity}]` per
 *   split — the backend uses exact allocations (no proportional fallback).
 *
 * Constraints (backend):
 *   - original shipment must be status='pending'
 *   - at least 2 splits
 *   - per-item allocations sum exactly to the parcel's item totals
 */
function addWorkingDaysISO(iso, n) {
  const d = iso ? new Date(iso) : new Date();
  let added = 0;
  while (added < n) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0) added += 1;
  }
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function SplitShipmentModal({ isOpen, onClose, onDone, shipment }) {
  const baseDate = (shipment?.scheduled_date && String(shipment.scheduled_date).slice(0, 10))
    || new Date().toISOString().slice(0, 10);

  // Items in this parcel. Pull from shipment.items[] (each {order_item_id,
  // product_name, qty, brand}). Single-item parcel = items.length === 1.
  const items = useMemo(() => shipment?.items || [], [shipment]);
  const isMultiItem = items.length > 1;

  // Legacy single-qty rows (used when isMultiItem === false).
  const half = Math.floor((shipment?.quantity || 2) / 2);
  const [singleRows, setSingleRows] = useState(() => [
    { quantity: half, scheduledDate: baseDate },
    { quantity: (shipment?.quantity || 2) - half, scheduledDate: addWorkingDaysISO(baseDate, 3) },
  ]);

  // Multi-item: parts is [{ scheduledDate, qtyByItem: { orderItemId → qty } }]
  const [parts, setParts] = useState(() => {
    if (!isMultiItem) return [];
    // Seed: split each item half-and-half across 2 parts.
    return [
      {
        scheduledDate: baseDate,
        qtyByItem: Object.fromEntries(items.map(it => [it.order_item_id, Math.floor((it.quantity || 0) / 2)])),
      },
      {
        scheduledDate: addWorkingDaysISO(baseDate, 3),
        qtyByItem: Object.fromEntries(items.map(it => [it.order_item_id, (it.quantity || 0) - Math.floor((it.quantity || 0) / 2)])),
      },
    ];
  });
  const [saving, setSaving] = useState(false);

  // ----- single-item helpers (unchanged) -----
  const singleSum = useMemo(
    () => singleRows.reduce((a, r) => a + (parseInt(r.quantity, 10) || 0), 0),
    [singleRows]
  );
  const singleMatches = singleSum === (shipment?.quantity || 0);

  function singleUpdate(i, field, value) {
    setSingleRows(prev => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  }
  function singleAddRow() {
    setSingleRows(prev => {
      const last = prev[prev.length - 1];
      return [...prev, { quantity: 0, scheduledDate: addWorkingDaysISO(last?.scheduledDate || baseDate, 3) }];
    });
  }
  function singleRemoveRow(i) {
    if (singleRows.length <= 2) return;
    setSingleRows(prev => prev.filter((_, idx) => idx !== i));
  }

  // ----- multi-item helpers -----
  function partUpdateQty(partIdx, orderItemId, value) {
    const v = Math.max(0, parseInt(value, 10) || 0);
    setParts(prev => prev.map((p, idx) =>
      idx === partIdx ? { ...p, qtyByItem: { ...p.qtyByItem, [orderItemId]: v } } : p
    ));
  }
  function partUpdateDate(partIdx, value) {
    setParts(prev => prev.map((p, idx) => (idx === partIdx ? { ...p, scheduledDate: value } : p)));
  }
  function partAdd() {
    setParts(prev => {
      const last = prev[prev.length - 1];
      return [...prev, {
        scheduledDate: addWorkingDaysISO(last?.scheduledDate || baseDate, 3),
        qtyByItem: Object.fromEntries(items.map(it => [it.order_item_id, 0])),
      }];
    });
  }
  function partRemove(partIdx) {
    if (parts.length <= 2) return;
    setParts(prev => prev.filter((_, idx) => idx !== partIdx));
  }

  // Per-item sum across all parts (for validation banner)
  const perItemSum = useMemo(() => {
    const out = {};
    for (const it of items) {
      out[it.order_item_id] = parts.reduce((a, p) => a + (p.qtyByItem[it.order_item_id] || 0), 0);
    }
    return out;
  }, [items, parts]);
  const allItemsMatch = items.every(it => perItemSum[it.order_item_id] === (it.quantity || 0));

  async function save() {
    setSaving(true);
    try {
      if (isMultiItem) {
        if (!allItemsMatch) {
          toast.error('Each product\'s quantities must sum to its parcel total');
          return;
        }
        if (parts.some(p => Object.values(p.qtyByItem).every(v => !v))) {
          toast.error('Each part must contain at least one item with a positive quantity');
          return;
        }
        await orderApi.splitShipment(shipment.id, {
          splits: parts.map(p => ({
            scheduledDate: p.scheduledDate,
            items: Object.entries(p.qtyByItem)
              .filter(([, q]) => q > 0)
              .map(([orderItemId, quantity]) => ({ orderItemId, quantity })),
          })),
        });
      } else {
        if (!singleMatches) {
          toast.error(`Quantities must sum to ${shipment.quantity} (currently ${singleSum})`);
          return;
        }
        if (singleRows.some(r => !r.quantity || r.quantity <= 0)) {
          toast.error('Every split must have a positive quantity');
          return;
        }
        await orderApi.splitShipment(shipment.id, {
          splits: singleRows.map(r => ({
            quantity: parseInt(r.quantity, 10),
            scheduledDate: r.scheduledDate,
          })),
        });
      }
      const partsCount = isMultiItem ? parts.length : singleRows.length;
      toast.success(`Split into ${partsCount} parts — each is now its own order`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Split failed');
    } finally { setSaving(false); }
  }

  if (!shipment) return null;

  // ----- render: single-item path (legacy) -----
  if (!isMultiItem) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Split shipment">
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            Split shipment <strong>#{shipment.shipment_number}</strong>
            (qty <strong>{shipment.quantity}</strong>) into multiple shipments, each
            with its own scheduled date.
          </p>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2">#</th>
                  <th className="text-left px-3 py-2">Quantity</th>
                  <th className="text-left px-3 py-2">Scheduled date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {singleRows.map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                    <td className="px-3 py-2">
                      <input type="number" min="1" value={r.quantity}
                             onChange={(e) => singleUpdate(i, 'quantity', e.target.value)}
                             className="w-24 px-2 py-1 border border-gray-300 rounded text-sm" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="date" value={r.scheduledDate}
                             onChange={(e) => singleUpdate(i, 'scheduledDate', e.target.value)}
                             className="px-2 py-1 border border-gray-300 rounded text-sm" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      {singleRows.length > 2 && (
                        <button onClick={() => singleRemoveRow(i)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between">
            <button onClick={singleAddRow} className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
              <Plus className="w-3.5 h-3.5" /> Add another split
            </button>
            <div className={`text-xs font-medium ${singleMatches ? 'text-green-700' : 'text-red-700'}`}>
              Sum: {singleSum} / {shipment.quantity}
              {singleMatches ? ' ✓' : ` (need ${shipment.quantity - singleSum > 0 ? '+' : ''}${shipment.quantity - singleSum})`}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving || !singleMatches}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Split
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // ----- render: multi-item path (per-item × per-part grid) -----
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Split shipment">
      <div className="space-y-3">
        <p className="text-sm text-gray-700">
          This parcel has <strong>{items.length} products</strong>. Set how much of
          each product goes in each part. Each row must sum to the parcel total
          for that product.
        </p>

        <div className="border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2 sticky left-0 bg-gray-50">Product</th>
                <th className="text-right px-3 py-2">In parcel</th>
                {parts.map((p, i) => (
                  <th key={i} className="text-left px-3 py-2 border-l border-gray-200">
                    <div className="flex items-center gap-1">
                      <span>Part {i + 1}</span>
                      {parts.length > 2 && (
                        <button onClick={() => partRemove(i)}
                                className="p-0.5 text-red-600 hover:bg-red-50 rounded"
                                title="Remove this part">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
              <tr>
                <th className="text-left px-3 py-2 sticky left-0 bg-gray-50 font-normal text-gray-500">Scheduled date</th>
                <th className="text-right px-3 py-2"></th>
                {parts.map((p, i) => (
                  <td key={i} className="px-3 py-2 border-l border-gray-200">
                    <input type="date" value={p.scheduledDate}
                           onChange={(e) => partUpdateDate(i, e.target.value)}
                           className="px-2 py-1 border border-gray-300 rounded text-xs" />
                  </td>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((it) => {
                const total = it.quantity || 0;
                const sum = perItemSum[it.order_item_id] || 0;
                const ok = sum === total;
                return (
                  <tr key={it.order_item_id}>
                    <td className="px-3 py-2 sticky left-0 bg-white">
                      <div className="font-medium text-gray-900">{it.product_name}</div>
                      {it.indian_brand && <div className="text-xs text-gray-500">{it.indian_brand}</div>}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">{total}</td>
                    {parts.map((p, i) => (
                      <td key={i} className="px-3 py-2 border-l border-gray-200">
                        <input type="number" min="0" max={total} value={p.qtyByItem[it.order_item_id] ?? 0}
                               onChange={(e) => partUpdateQty(i, it.order_item_id, e.target.value)}
                               className={`w-20 px-2 py-1 border rounded text-sm ${ok ? 'border-gray-300' : 'border-red-300 bg-red-50'}`} />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 text-xs">
              <tr>
                <td className="px-3 py-2 sticky left-0 bg-gray-50 font-medium text-gray-700">Row sums</td>
                <td className="px-3 py-2"></td>
                {items.length > 0 && parts.map((p, i) => (
                  <td key={i} className="px-3 py-2 border-l border-gray-200 text-gray-600">
                    {/* (per-part total qty across all items, informational only) */}
                    Total: <strong>{Object.values(p.qtyByItem).reduce((a, b) => a + (b || 0), 0)}</strong>
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="flex items-center justify-between">
          <button onClick={partAdd} className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
            <Plus className="w-3.5 h-3.5" /> Add another part
          </button>
          <div className={`text-xs font-medium ${allItemsMatch ? 'text-green-700' : 'text-red-700'}`}>
            {allItemsMatch ? 'All products balanced ✓' : 'Each row must sum to the parcel total'}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving || !allItemsMatch}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Split
          </button>
        </div>
      </div>
    </Modal>
  );
}
